import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertTriangle, ArrowRight, ClipboardList, Copy, Info, Layers, Loader2, Lock, Plus, PowerOff, RotateCcw, Search, ShieldCheck } from 'lucide-react';
import {
  adminBinderTemplates,
  type BinderTemplateBlindingScope,
  type BinderTemplateRow,
} from '@/api/client';
import { PageHeader } from '@/components/layout/PageHeader';
import { BlindingScopeBadge } from '@/components/binder/BlindingScopeBadge';
import { errorText, errorToneClass, isPermissionError } from '@/lib/permissionError';

/**
 * F8b - Binder Template Configuration console (platform_admin / application_admin).
 *
 * Templates decouple the ISF folder layout from the code, so a sponsor-
 * specific ISF index can be created once and re-used by every study for
 * that sponsor. This page lists all templates and lets an admin create,
 * deactivate, or drill into the editor for a template.
 *
 * The default template (BTP-0001, is_default='true') is read-only; the
 * UI hides delete + rename affordances for it and the BE refuses either
 * request. To customise the default's folders, clone it and edit the copy.
 */
export function BinderTemplatesListPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [scopeFilter, setScopeFilter] = useState<'all' | BinderTemplateBlindingScope>('all');
  const [includeHidden, setIncludeHidden] = useState(false);
  const [confirmingDeactivate, setConfirmingDeactivate] = useState<BinderTemplateRow | null>(null);

  const listQ = useQuery({
    queryKey: ['adminBinderTemplates', 'list', { includeHidden }],
    queryFn: () => adminBinderTemplates.list({ include_inactive: includeHidden }),
    staleTime: 30_000,
  });

  const deactivateM = useMutation({
    mutationFn: (id: string) => adminBinderTemplates.deactivate(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['adminBinderTemplates'] });
      toast.success('Template hidden — no longer shown when creating a study.');
      setConfirmingDeactivate(null);
    },
    onError: (err: unknown) => {
      toast.error(errorText(err, 'Hide failed'));
    },
  });

  const restoreM = useMutation({
    mutationFn: (id: string) => adminBinderTemplates.update(id, { is_active: true }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['adminBinderTemplates'] });
      toast.success('Template restored.');
    },
    onError: (err: unknown) => {
      toast.error(errorText(err, 'Restore failed'));
    },
  });

  const templates = listQ.data?.templates ?? [];

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    const scoped =
      scopeFilter === 'all' ? templates : templates.filter((t) => t.blinding_scope === scopeFilter);
    if (!s) return scoped;
    return scoped.filter(
      (t) =>
        t.name.toLowerCase().includes(s) ||
        t.reference_number.toLowerCase().includes(s) ||
        t.description.toLowerCase().includes(s),
    );
  }, [templates, search, scopeFilter]);

  const activeCount = templates.filter((t) => t.is_active === 'true').length;
  const inactiveCount = templates.length - activeCount;

  return (
    <div className="space-y-6">
      <PageHeader
        backTo="/admin"
        title="Binder templates"
        subtitle="Reusable folder layouts. Each study picks one at creation and its binder is built from that layout."
        actions={
          <Link to="/admin/binder-templates/new" className="btn-primary">
            <Plus className="h-4 w-4" /> New template
          </Link>
        }
      />

      <div className="grid gap-3 sm:grid-cols-3">
        <StatCard label="Total templates" value={listQ.isLoading ? '—' : templates.length} icon={Layers} />
        <StatCard label="Active" value={listQ.isLoading ? '—' : activeCount} icon={ShieldCheck} />
        <StatCard label="Hidden" value={listQ.isLoading ? '—' : inactiveCount} icon={PowerOff} />
      </div>

      <div className="card overflow-hidden">
        <div className="flex flex-wrap items-center gap-3 border-b border-surface-border px-4 py-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-muted" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, reference, or description…"
              className="input w-full pl-8"
            />
          </div>
          <select
            aria-label="Filter by template type"
            className="input w-auto"
            value={scopeFilter}
            onChange={(e) => setScopeFilter(e.target.value as 'all' | BinderTemplateBlindingScope)}
          >
            <option value="all">All types</option>
            <option value="blinded">Blinded</option>
            <option value="unblinded">Unblinded</option>
          </select>
          <label className="flex items-center gap-2 text-sm text-ink-secondary">
            <input
              type="checkbox"
              checked={includeHidden}
              onChange={(e) => setIncludeHidden(e.target.checked)}
            />
            Show inactive
          </label>
        </div>

        {listQ.isLoading ? (
          <div className="flex items-center justify-center gap-2 px-4 py-8 text-sm text-ink-muted">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading templates…
          </div>
        ) : listQ.isError ? (
          <div className={`m-4 flex items-start gap-2 rounded border p-4 text-sm ${errorToneClass(listQ.error)}`}>
            {isPermissionError(listQ.error) ? <Info className="mt-0.5 h-4 w-4 shrink-0"/> : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />}
            <span>
              {errorText(listQ.error, 'Failed to load templates.')}
            </span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-ink-muted">
            {search.trim() || scopeFilter !== 'all'
              ? 'No templates match the current filters.'
              : 'No templates yet. Create the first one above.'}
          </div>
        ) : (
          <ul className="divide-y divide-surface-border">
            {filtered.map((t) => (
              <li
                key={t.template_id}
                className="flex items-center gap-3 px-4 py-3 hover:bg-surface-muted/40"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <Link
                      to={`/admin/binder-templates/${t.template_id}`}
                      className="text-sm font-medium text-ink-primary hover:underline"
                    >
                      {t.name}
                    </Link>
                    <span className="badge">{t.reference_number}</span>
                    <BlindingScopeBadge scope={t.blinding_scope} />
                    {t.scope === 'site' ? (
                      <span className="badge bg-success-100 text-success-800">Yours</span>
                    ) : (
                      <span className="badge bg-surface-muted text-ink-muted">Platform</span>
                    )}
                    {t.is_default === 'true' && (
                      <span className="badge bg-accent-100 text-accent-700">
                        <Lock className="h-3 w-3" /> Default
                      </span>
                    )}
                    {t.is_active !== 'true' && (
                      <span className="badge bg-danger-100 text-danger-700">Hidden</span>
                    )}
                    {/* CR-2026-09-29 — a template that has not routed every
                        mandatory document to one of its folders. Uploads on a
                        study using it would never file into the binder. */}
                    {t.readiness && !t.readiness.ready && (
                      <span
                        className="badge bg-amber-100 text-amber-800"
                        title={`Not routed yet: ${t.readiness.missing.map((m) => m.name).join(', ')}. Open the template and choose a folder for each.`}
                      >
                        <AlertTriangle className="h-3 w-3" /> Not ready
                      </span>
                    )}
                  </div>
                  {t.description && (
                    <p className="mt-0.5 text-xs text-ink-muted line-clamp-2">{t.description}</p>
                  )}
                </div>
                <button
                  type="button"
                  className="btn-secondary text-xs"
                  onClick={() => navigate(`/admin/binder-templates/${t.template_id}`)}
                >
                  Open <ArrowRight className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  className="btn-secondary text-xs"
                  onClick={() =>
                    navigate(
                      `/admin/binder-templates/new?clone_from=${encodeURIComponent(t.template_id)}&name=${encodeURIComponent(`${t.name} (copy)`)}`,
                    )
                  }
                  title="Create a new template starting from this one."
                >
                  <Copy className="h-3.5 w-3.5" /> Duplicate
                </button>
                {t.is_default !== 'true' && t.is_active === 'true' && (
                  <button
                    type="button"
                    className="btn-secondary text-xs text-danger-700"
                    onClick={() => setConfirmingDeactivate(t)}
                  >
                    <PowerOff className="h-3.5 w-3.5" /> Hide
                  </button>
                )}
                {t.is_default !== 'true' && t.is_active !== 'true' && (
                  <button
                    type="button"
                    className="btn-secondary text-xs"
                    disabled={restoreM.isPending}
                    onClick={() => restoreM.mutate(t.template_id)}
                  >
                    <RotateCcw className="h-3.5 w-3.5" /> Restore
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {confirmingDeactivate && (
        <ConfirmDeactivateDialog
          template={confirmingDeactivate}
          onCancel={() => setConfirmingDeactivate(null)}
          onConfirm={() => deactivateM.mutate(confirmingDeactivate.template_id)}
          isPending={deactivateM.isPending}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// StatCard
// ---------------------------------------------------------------------------

function StatCard({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: string | number;
  icon: typeof ShieldCheck;
}) {
  return (
    <div className="card p-4">
      <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-ink-muted">
        <Icon className="h-3.5 w-3.5" />
        {label}
      </div>
      <div className="mt-1 text-[24px] font-semibold text-ink-primary">{value}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ConfirmDeactivateDialog
// ---------------------------------------------------------------------------

function ConfirmDeactivateDialog({
  template,
  onCancel,
  onConfirm,
  isPending,
}: {
  template: BinderTemplateRow;
  onCancel: () => void;
  onConfirm: () => void;
  isPending: boolean;
}) {
  const usageQ = useQuery({
    queryKey: ['adminBinderTemplates', 'usage', template.template_id],
    queryFn: () => adminBinderTemplates.usage(template.template_id),
    staleTime: 10_000,
  });
  const activeCount = usageQ.data?.active_studies ?? 0;
  const sampleNames = usageQ.data?.sample_names ?? [];
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onCancel}>
      <div className="w-full max-w-md rounded-lg border border-surface-border bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="border-b border-surface-border px-5 py-4">
          <h2 className="flex items-center gap-2 text-base font-semibold text-ink-primary">
            <ClipboardList className="h-4 w-4" /> Hide template
          </h2>
        </div>
        <div className="space-y-3 px-5 py-4 text-sm text-ink-secondary">
          <p>
            Hide <b>{template.name}</b>? Studies that already use it keep their layout — this
            only removes it from the picker when starting new studies.
          </p>
          {usageQ.isLoading ? (
            <p className="flex items-center gap-2 rounded border border-surface-border bg-surface-muted/30 p-3 text-xs text-ink-muted">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking usage…
            </p>
          ) : activeCount > 0 ? (
            <div className="rounded border border-warning-200 bg-warning-50 p-3 text-xs text-warning-800">
              <div className="font-medium">
                In use by {activeCount} stud{activeCount === 1 ? 'y' : 'ies'}.
              </div>
              {sampleNames.length > 0 && (
                <div className="mt-1 text-warning-700">
                  {sampleNames.slice(0, 3).join(' · ')}
                  {activeCount > 3 ? ` · +${activeCount - 3} more` : ''}
                </div>
              )}
            </div>
          ) : (
            <p className="rounded border border-surface-border bg-surface-muted/30 p-3 text-xs text-ink-muted">
              Not currently used by any study.
            </p>
          )}
        </div>
        <div className="flex justify-end gap-2 border-t border-surface-border px-5 py-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={isPending}
            className="rounded border border-surface-border px-3 py-1.5 text-sm font-medium text-ink-primary hover:bg-surface-muted/50 disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isPending}
            className="inline-flex items-center gap-1.5 rounded bg-danger-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-danger-700 disabled:opacity-60"
          >
            {isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Hide
          </button>
        </div>
      </div>
    </div>
  );
}
